import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Space, Select, Modal, message } from 'antd';
import 'antd/dist/reset.css';

import axiosInstance from '../utils/axios_setup';
import { apiEndpoints } from '../utils/urls';
import '../styles/home.css';

const Home = () => {
    const cooldownDuration = 20000; // 20 seconds

    const [emailCount, setEmailCount] = useState(0);
    const [isCooldown, setIsCooldown] = useState(false);
    const [remainingTime, setRemainingTime] = useState(20);
    const [response, setResponse] = useState({ message: '', className: '' });
    const [htmlFile, setHtmlFile] = useState(null);
    const [jsonFile, setJsonFile] = useState(null);
    const [receiverEmails, setReceiverEmails] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [emailSentMessage, setEmailSentMessage] = useState('');
    const [isResendEnabled, setIsResendEnabled] = useState(true);
    const [resendCountdown, setResendCountdown] = useState(10);
    const [emailAddedSuccessfully, setEmailAddedSuccessfully] = useState(false);

    // Fetch email count and trusted entities on mount
    useEffect(() => {
        // Fetch email count
        axiosInstance
        .get(apiEndpoints.emailCount)
        .then((res) => {
            setEmailCount(res.data.data.count);
        })
        .catch((err) => {
            console.error(err);
        });

        // Fetch trusted entities
        axiosInstance
        .get(apiEndpoints.trustedEntities)
        .then((res) => {
            setReceiverEmails(res.data.data.emails);
        })
        .catch((err) => {
            console.error(err);
        });
    }, []);

    const handleFileChange = (setter) => (event) => setter(event.target.files[0]);

    const handleFormSubmit = (values) => {
        if (isCooldown) return;

        const formData = new FormData();
        formData.append('receivers', JSON.stringify(values.receivers));
        formData.append('subject', values.subject);
        formData.append('file', htmlFile);
        if (jsonFile) formData.append('json', jsonFile);

        axiosInstance
        .post(apiEndpoints.sendMail, formData, {
            headers: {
            'Content-Type': 'multipart/form-data',
            },
        })
        .then((res) => {
            const { status, message, data: responseData } = res.data;
            if (status === 'success') {
                setEmailCount(responseData.count);
                setResponse({
                    message: `Success: ${message}. Message ID: ${responseData.message_id}`,
                    className: 'response-success',
                });
                startCooldown();
            } else {
                setResponse({
                    message: `Failed: ${message}. Details: ${res.data.errors}`,
                    className: 'response-failure',
                });
            }
        })
        .catch((error) => {
            if (error.response.status === 400) {
                const { message } = error.response.data;
                setResponse({
                    message: `Failed: ${message}. Details: ${error.response.data.errors}`,
                    className: 'response-failure',
                });
            } else {
                setResponse({
                    message: `Error: ${error.response?.data?.message || error.message}`,
                    className: 'response-failure',
                });
            }
        });
    };


  const startCooldown = () => {
    setIsCooldown(true);
    const endTime = Date.now() + cooldownDuration;
    const interval = setInterval(() => {
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      setRemainingTime(Math.max(0, remaining));

      if (remaining <= 0) {
        setIsCooldown(false);
        clearInterval(interval);
        setResponse({ message: '', className: '' });
      }
    }, 1000);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    if (emailAddedSuccessfully) {
      axiosInstance
        .get(apiEndpoints.trustedEntities)
        .then((res) => {
          setReceiverEmails(res.data.data.emails);
          setEmailAddedSuccessfully(false);
        })
        .catch((err) => {
          console.error('Failed to refresh emails list:', err);
        });
    }
    setUserEmail('');
    setEmailSentMessage('');
    setIsModalVisible(false);
  };

  const startResendCountdown = () => {
    setIsResendEnabled(false);
    setResendCountdown(10);
    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleEmailSubmit = () => {
    if (!userEmail) {
      message.error('Please enter a valid email address.');
      return;
    }

    axiosInstance
      .post(apiEndpoints.addEmail, { email: userEmail })
      .then((res) => {
        if (res.status === 200) {
          setEmailSentMessage(
            'A verification email has been sent to your email. Click it to verify and add your email to the list. refrsh the page to see the updated list.'
          );
          startResendCountdown();
          setEmailAddedSuccessfully(true);
        }
      })
      .catch((err) => {
        message.error(err.response?.data?.message || 'Failed to send email. Please try again.');
      });
  };


  return (
    <div className="App" style={styles.container}>
      <h1 style={styles.header}>Send Email</h1>
      <div style={styles.emailCount}>Emails sent today: {emailCount} / 95</div>

      <Form layout="vertical" style={styles.form} onFinish={handleFormSubmit}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Form.Item
            label="Receivers"
            name="receivers"
            rules={[{ required: true, message: 'Please select at least one email.' }]}
          >
            <Select
              mode="tags"
              placeholder="Select Emails"
              options={receiverEmails.map((email) => ({ value: email, label: email }))}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: 'Please enter the email subject.' }]}
          >
            <Input placeholder="Email Subject" />
          </Form.Item>
          <Form.Item
            label="HTML File"
            name="htmlFile"
            rules={[{ required: true, message: 'Please select an HTML file to send.' }]}
          >
            <Input type="file" accept=".html" onChange={handleFileChange(setHtmlFile)} />
          </Form.Item>
          <Form.Item label="JSON Data File (optional data file for template)" name="jsonFile">
            <Input type="file" accept=".json" onChange={handleFileChange(setJsonFile)} />
          </Form.Item>
          <Button type="primary" htmlType="submit" disabled={isCooldown} style={{ width: '100%' }}>
            {isCooldown ? `Wait ${remainingTime}s` : 'Send Email'}
          </Button>
          <Button
            type="default"
            onClick={showModal}
            style={{ width: '100%', marginTop: '10px' }}
          >
            Want your email here?
          </Button>
        </Space>
      </Form>

      {response.message && (
        <div className={response.className} style={styles.response}>
          {response.message}
        </div>
      )}

    <Modal
      title="Add Your Email"
      open={isModalVisible}
      onOk={handleEmailSubmit}
      onCancel={handleCancel}
      footer={[
        isResendEnabled ? (
          <Button type="primary" onClick={handleEmailSubmit} key="submit">
            Submit
          </Button>
        ) : (
          <div key="countdown" style={{ color: 'gray', textAlign: 'center' }}>
            You can submit again in {resendCountdown}s
          </div>
        ),
      ]}
      okText="Submit"
    >
      <Input
        placeholder="Enter your email address"
        value={userEmail}
        onChange={(e) => setUserEmail(e.target.value)}
        style={{ marginBottom: '10px' }}
      />
      {emailSentMessage && (
        <div style={{ marginTop: '10px', color: 'green', fontWeight: 'bold' }}>
          {emailSentMessage}
        </div>
      )}
    </Modal>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '30px auto',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
  },
  header: {
    textAlign: 'center',
    color: '#444',
  },
  emailCount: {
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#e2e3e5',
    color: '#383d41',
    border: '1px solid #b9bbbe',
    borderRadius: '4px',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  form: {
    marginTop: '20px',
  },
  response: {
    marginTop: '20px',
    padding: '10px',
    borderRadius: '4px',
    textAlign: 'center',
  },
};

export default Home;
